import { FC } from "react";
import { useRouter } from "next/router";
import {
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Button,
  Text,
  Flex,
} from "@chakra-ui/react";
import { ChevronDownIcon } from "@chakra-ui/icons";
import { SUPPORTED_LOCALES, Locale } from "@/constants/landingPages";

// Language display names
const LANGUAGE_NAMES: Record<Locale, string> = {
  en: "English",
  de: "Deutsch",
  fr: "Français",
  es: "Español",
  it: "Italiano",
  pt: "Português",
  sk: "Slovenčina",
  cs: "Čeština",
  nl: "Nederlands",
  sv: "Svenska",
  pl: "Polski",
};

// Language flags (emoji or can be replaced with icons)
const LANGUAGE_FLAGS: Record<Locale, string> = {
  en: "🇬🇧",
  de: "🇩🇪",
  fr: "🇫🇷",
  es: "🇪🇸",
  it: "🇮🇹",
  pt: "🇵🇹",
  sk: "🇸🇰",
  cs: "🇨🇿",
  nl: "🇳🇱",
  sv: "🇸🇪",
  pl: "🇵🇱",
};

interface LanguagePickerProps {
  currentLocale: Locale;
}

export const LanguagePicker: FC<LanguagePickerProps> = ({ currentLocale }) => {
  const router = useRouter();

  const handleLanguageChange = (newLocale: Locale) => {
    // Parse current path to determine structure
    const pathParts = router.asPath.split("/").filter(Boolean);

    // Check if we're on a landing page (has platform in path)
    // Possible patterns:
    // - /{locale} -> home page
    // - /{locale}/{platform} -> landing page

    let newPath = "";

    if (pathParts.length === 1) {
      // Home page: /{locale}
      newPath = `/${newLocale}`;
    } else if (pathParts.length >= 2) {
      // Landing page: /{locale}/{platform} or deeper
      const platform = pathParts[1];
      const remainingPath = pathParts.slice(2).join("/");
      newPath = remainingPath
        ? `/${newLocale}/${platform}/${remainingPath}`
        : `/${newLocale}/${platform}`;
    } else {
      // Fallback to home
      newPath = `/${newLocale}`;
    }

    router.push(newPath);
  };

  return (
    <Menu>
      <MenuButton
        as={Button}
        variant="ghost"
        size="sm"
        rightIcon={<ChevronDownIcon />}
        _hover={{ bg: "gray.100" }}
        _active={{ bg: "gray.200" }}
      >
        <Flex align="center" gap={2}>
          <Text fontSize="lg">{LANGUAGE_FLAGS[currentLocale]}</Text>
          <Text display={{ base: "none", md: "block" }} fontSize="sm">
            {LANGUAGE_NAMES[currentLocale]}
          </Text>
        </Flex>
      </MenuButton>
      <MenuList maxH="400px" overflowY="auto">
        {SUPPORTED_LOCALES.map((locale) => (
          <MenuItem
            key={locale}
            onClick={() => handleLanguageChange(locale)}
            bg={locale === currentLocale ? "gray.100" : "transparent"}
            fontWeight={locale === currentLocale ? "bold" : "normal"}
          >
            <Flex align="center" gap={3}>
              <Text fontSize="lg">{LANGUAGE_FLAGS[locale]}</Text>
              <Text>{LANGUAGE_NAMES[locale]}</Text>
            </Flex>
          </MenuItem>
        ))}
      </MenuList>
    </Menu>
  );
};
